<script setup>
import { ref, onMounted, computed, watch, inject } from 'vue';
import { RequestService } from '../../services/RequestService';
import BcsModal from '../BcsModal.vue';
import BcsSearchDropdown from '../BcsSearchDropdown.vue';

const functionsBaseUrl = inject('functionsBaseUrl')
const { handleErrors, simpleGet, put } = RequestService()

const questions = ref([])
const filteredQuestions = ref([])
const loading = ref(false)
const error = ref(null)
const selectedQuestion = ref(null)
const modalRef = ref(null)
const editModalRef = ref(null)
const successModalRef = ref(null)
const currentPage = ref(1)
const itemsPerPage = 10
const isSearchActive = ref(false)
const editingQuestion = ref(null)
const editSaving = ref(false)
const editForm = ref({
  id: '',
  question: ''
})

const isFormValid = computed(() => {
  return editForm.value.question.trim() !== ''
})

const totalQuestions = computed(() => {
  return questions.value.length
});

const paginatedQuestions = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const sourceArray = isSearchActive.value ? filteredQuestions.value : questions.value
  return sourceArray.slice(startIndex, endIndex)
})

const totalPages = computed(() => {
  const sourceArray = isSearchActive.value ? filteredQuestions.value : questions.value
  return Math.ceil(sourceArray.length / itemsPerPage)
})

const emit = defineEmits(['update:totalQuestions'])

watch(totalQuestions, (newTotal) => {
  emit('update:totalQuestions', newTotal)
}, { immediate: true })

async function getAllQuestions() {
  try {
    loading.value = true;
    error.value = null;

    const response = await simpleGet(
      'getCopyManagerQuestions',
      `${functionsBaseUrl}/utilityGateway/api/v1/copy-manager/questions`,
      { rethrow: true }
    )

    questions.value = response?.data?.data || []
    
    loading.value = false
    return questions.value
  } catch (err) {
    error.value = 'Error loading questions'
    loading.value = false
    handleErrors('Error fetching questions:', err)
    return []
  }
}

function handleView(question) {
  selectedQuestion.value = question
  modalRef.value.openModal()
}

function handleEdit(question) {
  editingQuestion.value = question
  editForm.value = {
    id: question.id,
    question: question.question || ''
  }
  editModalRef.value.openModal()
}

async function handleSaveEdit() {
  if (!editingQuestion.value) return
  
  try {
    editSaving.value = true
    const response = await put(
      'updateCopyManagerQuestion',
      `${functionsBaseUrl}/utilityGateway/api/v1/copy-manager/questions/${encodeURIComponent(editingQuestion.value.id)}`,
      { question: editForm.value.question }
    )
    if (!response) return

    const index = questions.value.findIndex(q => q.id === editingQuestion.value.id)
    if (index !== -1) {
      questions.value[index].question = editForm.value.question
    }
    
    editModalRef.value.closeModal()
    successModalRef.value.openModal()
  } catch (error) {
    handleErrors('Error saving question:', error)
  } finally {
    editSaving.value = false
  }
}

function handleCancelEdit() {
  editModalRef.value.closeModal()
}

function handleSearch({ results }) {
  filteredQuestions.value = results
  isSearchActive.value = true
  currentPage.value = 1
}

function handleClear() {
  isSearchActive.value = false
  filteredQuestions.value = []
  currentPage.value = 1
}

onMounted(() => {
  getAllQuestions()
})
</script>

<template>
  <div class="questions-manager-content pe-4 me-0">
    <!-- Search component -->                
    <div class="search-container mx-4 mb-4 mt-4">
      <BcsSearchDropdown 
        placeholder="Search questions..."
        :items="questions"
        :searchFields="['question']"
        arrayTerm="question"
        @search="handleSearch"
        @clear="handleClear"
        :tooltipContent="{ date: new Date(), events: [{ id: 1, name: 'Use the search function to find the existing text you\u2019re looking to update', body: '', date: new Date() }] }"
        :show-arrow="true"
      />
    </div>
    
    <div v-if="loading" class="text-center p-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading questions data...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger mx-4" role="alert">
      <i class="bi bi-exclamation-triangle"></i>
      {{ error }}
    </div>

    <!-- Questions List -->
    <div v-else class="questions-container mx-4 mt-3">
      <div v-if="questions.length === 0" class="text-center py-4 text-muted">
        <i class="bi bi-inbox fs-1 d-block mb-2"></i>
        No questions available
      </div>
      
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead class="table-light">
            <tr>
              <th scope="col" style="width: 20%;">ID</th>
              <th scope="col" style="width: 65%;">Question</th>
              <th scope="col" style="width: 15%;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="question in paginatedQuestions" :key="question.id">
              <td>
                <div class="small fw-bold">{{ question.id || 'N/A' }}</div>
              </td>
              <td>
                <div class="content-preview text-primary text-14 fw-bold" :title="question.question">
                  {{ question.question ? (question.question.length > 100 ? question.question.substring(0, 100) + '...' : question.question) : 'No question' }}
                </div>
              </td>
              <td>
                <div class="btn-group btn-group-sm">
                  <button 
                    @click="handleView(question)"
                    class="btn btn-outline-primary btn-sm"
                    title="View">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button 
                    @click="handleEdit(question)"
                    class="btn btn-outline-secondary btn-sm"
                    title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
          <nav aria-label="Question pagination">
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button 
                  @click="currentPage--" 
                  class="page-link" 
                  :disabled="currentPage === 1"
                  aria-label="Previous">
                  <span aria-hidden="true">&laquo;</span>
                </button>
              </li>
              
              <li 
                v-for="page in totalPages" 
                :key="page" 
                class="page-item" 
                :class="{ active: page === currentPage }">
                <button @click="currentPage = page" class="page-link">{{ page }}</button>
              </li>
              
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button 
                  @click="currentPage++" 
                  class="page-link" 
                  :disabled="currentPage === totalPages"
                  aria-label="Next">
                  <span aria-hidden="true">&raquo;</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    
    <BcsModal 
      ref="modalRef" 
      modalTitle="Question Details" 
      modalSize="large"
      :hideCloseButton="false"
    >
      <div v-if="selectedQuestion" class="question-details p-3">
        <div class="mb-4">
          <h5 class="text-primary mb-2">Question</h5>
          <div class="p-3 border rounded bg-light">
            {{ selectedQuestion.question }}
          </div>
        </div>
        
        <div class="mb-4">
          <h5 class="text-primary mb-2">ID</h5>
          <div class="p-3 border rounded bg-light">
            {{ selectedQuestion.id || 'N/A' }}
          </div>
        </div>
      </div>
    </BcsModal>
    
    <!-- Edit Modal -->
    <BcsModal
      ref="editModalRef"
      modalType="info"
      modalTitle="Edit Question"
      modalSize="medium"
      :hideCloseButton="false"
    >
      <template v-if="editingQuestion">
        <div class="edit-question-form">
          <form @submit.prevent="handleSaveEdit">
            <div class="mb-3">
              <label class="form-label text-primary subtitle-modal">ID</label>
              <input 
                type="text" 
                class="form-control" 
                v-model="editForm.id"
                readonly
                disabled
              >
            </div>
            
            <div class="mb-3">
              <label class="form-label text-primary subtitle-modal">Question</label>
              <textarea 
                class="form-control content-textarea" 
                v-model="editForm.question"
                rows="10"
                placeholder="Enter question here..."
                required
              ></textarea>
            </div>
            
            <div class="d-flex justify-content-end gap-2 mt-4">
              <button 
                type="button" 
                class="btn btn-secondary" 
                @click="handleCancelEdit"
                :disabled="editSaving"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                class="btn btn-primary"
                :disabled="editSaving || !isFormValid"
              >
                <span v-if="editSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ editSaving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </template>
    </BcsModal>
    
    <!-- Success Modal -->
    <BcsModal ref="successModalRef" modalType="success" modalSize="small" :hideCloseButton="false" customClass="success-modal"> 
      <div class="text-center success-content"> 
        <h4 class="text-success mb-2">Question Updated Successfully!</h4> 
        <p class="text-muted">Your changes have been saved and updated in real-time.</p> 
      </div> 
    </BcsModal>
  </div>
</template>

<style scoped lang="scss">
.questions-manager-content {
  width: 100%;
}

.content-preview {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.question-details {
  h5 {
    font-weight: 600;
  }
}

.subtitle-modal {
  font-size: 16px;   
  font-weight: 500; 
}

.edit-question-form {
  text-align: left;
}

.content-textarea {
  min-height: 200px;
  resize: vertical;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.7;
}

.success-content {
  padding: 2rem 1rem;
  
  h4 {
    font-weight: 600;
  }
}

:deep {
  .medium {
    max-width: 85% !important;
  }

  .alert-content {
    margin-bottom: 0 !important;
  }

  .modal-content:not(.success-modal) {
    border-radius: 4px !important;
    height: auto;

    .modal-body {
      height: calc(100vh - 200px) !important;
      overflow-y: auto;
    }
  }
  
  .small {
    max-width: 400px !important;
  }
}
</style>
